<template>
    <div class="container-fluid img_backgrond">
        <div class="row">
            <div class="login-content mt-5">
                <div class="row">
                    <div class="col-sm-12 mt-4">
                        <h2 class="text-center">
                            <router-link :to="{ name: 'login' }"><img src="../../img/id-plein.png" alt="Logo"></router-link>
                        </h2>
                    </div>
                    <div class="col-sm-12 mt-4">
                        <h3 class="page-name text-center">{{ uLang('app.login_otp_title') }}</h3>
                    </div>
                </div>

                <div class="col-md-12">
                    <p class="user-message text-center">{{ uLang('app.login_otp_subtitle', { email: maskedEmail }) }}</p>
                </div>

                <form class="form-horizontal" @submit.prevent="onSubmit" @keydown="form.errors.clear($event.target.name)">
                    <div class="col-md-12">
                        <div class="form-group" v-bind:class="{ 'has-error' : form.errors.has('code') }">
                            <label for="code">{{ uLang('app.login_otp_code_label') }}</label>
                            <input
                                id="code"
                                type="text"
                                class="form-control otp-input"
                                name="code"
                                inputmode="numeric"
                                pattern="[0-9]*"
                                maxlength="6"
                                autocomplete="one-time-code"
                                v-model="form.code"
                                required
                            >
                            <span class="help-block" v-if="form.errors.has('code')" v-text="form.errors.get('code')"></span>
                        </div>
                    </div>

                    <div class="col-12">
                        <div class="form-group">
                            <input
                                type="submit"
                                :disabled="form.busy || !isCodeComplete"
                                :value="uLang('app.login_otp_verify')"
                                class="btn btn-brand btn-block"
                            />
                        </div>
                    </div>
                </form>

                <div class="col-12 text-center resend-section">
                    <button
                        type="button"
                        class="btn btn-link resend-btn"
                        :disabled="resendBusy || resendCountdown > 0 || resendsRemaining <= 0"
                        @click="onResend"
                    >
                        <span v-if="resendCountdown > 0">
                            {{ uLang('app.login_otp_resend_in', { seconds: resendCountdown }) }}
                        </span>
                        <span v-else-if="resendsRemaining <= 0">
                            {{ uLang('app.login_otp_resend_limit_reached') }}
                        </span>
                        <span v-else>
                            {{ uLang('app.login_otp_resend') }}
                        </span>
                    </button>
                </div>

                <div class="col-12 text-center">
                    <router-link :to="{ name: 'login' }" class="back-to-login">{{ uLang('app.login_otp_back_to_login') }}</router-link>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Form from '~/Form/Form'
import { SuccessAlert } from "~/Swals";

export default {
    name: "verify-otp",
    data() {
        return {
            form: new Form({
                challenge_token: '',
                code: '',
            }, true),
            maskedEmail: '',
            resendCountdown: 0,
            resendsRemaining: 0,
            resendBusy: false,
            countdownTimer: null,
        }
    },
    computed: {
        isCodeComplete() {
            return /^\d{6}$/.test(this.form.code);
        },
    },
    methods: {
        loadSessionMeta() {
            const raw = sessionStorage.getItem('login_otp');

            if (!raw) {
                return;
            }

            try {
                const meta = JSON.parse(raw);
                this.maskedEmail = meta.masked_email || '';
                this.resendCountdown = meta.resend_available_in || 0;
                this.resendsRemaining = meta.resends_remaining ?? 0;
            } catch (e) {
                sessionStorage.removeItem('login_otp');
            }
        },
        startCountdown() {
            if (this.countdownTimer) {
                clearInterval(this.countdownTimer);
            }

            if (this.resendCountdown <= 0) {
                return;
            }

            this.countdownTimer = setInterval(() => {
                if (this.resendCountdown > 0) {
                    this.resendCountdown--;
                } else {
                    clearInterval(this.countdownTimer);
                    this.countdownTimer = null;
                }
            }, 1000);
        },
        updateSessionMeta(data) {
            const meta = {
                masked_email: this.maskedEmail,
                expires_in: data.expires_in,
                resend_available_in: data.resend_available_in,
                resends_remaining: data.resends_remaining,
            };

            sessionStorage.setItem('login_otp', JSON.stringify(meta));
        },
        async onSubmit() {
            try {
                const data = await this.form.post('/api/login/verify-otp');

                sessionStorage.removeItem('login_otp');

                this.$store.dispatch('saveToken', {
                    token: data.token,
                    remember: false,
                });

                await this.$store.dispatch('fetchUser');

                this.$router.push({
                    name: 'dashboard',
                });
            } catch (e) {
                this.form.code = '';
            }
        },
        async onResend() {
            if (this.resendBusy || this.resendCountdown > 0 || this.resendsRemaining <= 0) {
                return;
            }

            this.resendBusy = true;

            try {
                const { data } = await window.axios.post('/api/login/resend-otp', {
                    challenge_token: this.form.challenge_token,
                });

                this.resendsRemaining = data.resends_remaining;
                this.resendCountdown = data.resend_available_in || 0;
                this.updateSessionMeta(data);
                this.startCountdown();

                new SuccessAlert({ text: data.message }).show();
            } catch (error) {
                const response = error.response;

                if (response && response.status === 429 && response.data) {
                    this.resendCountdown = response.data.resend_available_in || 60;
                    this.resendsRemaining = response.data.resends_remaining ?? this.resendsRemaining;
                    this.startCountdown();
                }
            } finally {
                this.resendBusy = false;
            }
        },
    },
    mounted() {
        if (this.$store.getters.authToken) {
            this.$store.dispatch('resetUser');
        }

        const token = this.$route.query.token;

        if (!token) {
            this.$router.replace({ name: 'login' });
            return;
        }

        this.form.challenge_token = token;
        this.loadSessionMeta();
        this.startCountdown();
    },
    destroyed() {
        if (this.countdownTimer) {
            clearInterval(this.countdownTimer);
        }
    },
}
</script>

<style lang="scss" scoped>
.has-error {
    color: red;

    input {
        border-color: red;
    }
}

.login-content {
    margin-top: 7%;
    margin-bottom: 7%;
    padding-bottom: 20px;
    box-shadow: 0 0 20px #ccc;
    background-size: 100% 100%;
    border-radius: 7px;
}

.user-message {
    padding: 15px 0;
    font-size: 14px;
    color: #777;
}

.img_backgrond {
    background-image: url("../../img/pages/Login-03-01.png");
    background-size: cover;
    height: 100vh;
    width: 100%;
}

.login-content {
    margin: 0 auto;
    width: 330px;
    max-width: 330px;

    @media screen and (max-width: 360px) {
        margin: 0 15px;
    }
}

.otp-input {
    font-size: 24px;
    letter-spacing: 8px;
    text-align: center;
}

.resend-section {
    margin-top: 8px;
    margin-bottom: 8px;
}

.resend-btn {
    font-size: 14px;
    padding: 0;
}

.back-to-login {
    color: #ccc;
    font-size: 15px;
}
</style>
